/**
 * Quick-connect: dragging from a node's connection handle onto empty canvas
 * creates a new connected node at the drop point (draw.io / Excalidraw staple).
 *
 * This module holds the PURE construction logic (node + connecting edge) so it
 * can be unit-tested without React Flow. The editor wires it to React Flow's
 * `onConnectStart`/`onConnectEnd` and supplies the drop point in flow
 * coordinates (via `screenToFlowPosition`).
 */
import type { NodeType } from '../types/schema'
import { snapToGridValue } from './grid'

export interface QuickConnectOpts {
  /** Id of the node the connection drag started from. */
  sourceId: string
  /** Drop point X in flow coordinates. */
  dropFlowX: number
  /** Drop point Y in flow coordinates. */
  dropFlowY: number
  /** Grid spacing for snap. Pass 0 (or a falsy value) to disable snapping. */
  gridSize: number
  /** Node type for the created node. Defaults to 'component'. */
  nodeType?: NodeType
  /** Layer the new node + edge belong to (draw.io-style layers). */
  layerId?: string
  /** Handle id the drag started from, threaded onto the edge's sourceHandle. */
  sourceHandle?: string
  /** Override id generation (used by tests for determinism). */
  idFactory?: () => string
}

let seq = 0
const defaultId = () => `qc-${Date.now()}-${(++seq).toString(36)}`

export interface QuickConnectNode {
  id: string
  type: string
  position: { x: number; y: number }
  data: Record<string, unknown>
  width: number
  height: number
  style: Record<string, unknown>
}

export interface QuickConnectEdge {
  id: string
  source: string
  target: string
  type: string
  sourceHandle?: string
  data: Record<string, unknown>
}

export interface QuickConnectResult {
  node: QuickConnectNode
  edge: QuickConnectEdge
}

/**
 * Build the new node + connecting edge for a drag-to-empty-canvas connect.
 *
 * The node mirrors the editor's own add-node shape (icon node => `universal`
 * type, 120x120) so it renders and round-trips identically to a node added via
 * the palette. The drop point is snapped to the grid when `gridSize` is set.
 */
export function buildQuickConnectNode(opts: QuickConnectOpts): QuickConnectResult {
  const id = (opts.idFactory ?? defaultId)()
  const nodeType: NodeType = opts.nodeType ?? 'component'

  const node: QuickConnectNode = {
    id,
    type: 'universal',
    position: {
      x: snapToGridValue(opts.dropFlowX, opts.gridSize),
      y: snapToGridValue(opts.dropFlowY, opts.gridSize),
    },
    data: {
      label: nodeType.charAt(0).toUpperCase() + nodeType.slice(1),
      nodeType,
      layerId: opts.layerId,
      bgColor: 'transparent',
      borderColor: 'transparent',
      borderWidth: 0,
      fillOpacity: 1,
      shadow: false,
    },
    width: 120,
    height: 120,
    style: {},
  }

  const edge: QuickConnectEdge = {
    id: `e-${opts.sourceId}-${id}`,
    source: opts.sourceId,
    target: id,
    type: 'custom',
    sourceHandle: opts.sourceHandle,
    data: { layerId: opts.layerId },
  }

  return { node, edge }
}
